import isNumber from 'lodash/isNumber';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import sortBy from 'lodash/sortBy';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  convertNewtonsToKgs,
  convertNewtonsToLbs,
} from '../../../utils/conversions';
import { roundValue } from '../../../utils/math';
import { Entity, EntityType, IAthlete } from '../../types';
import { entitiesProvider, IEntities } from '../entitiesProvider';
import {
  IWeightReportSettings,
  weightReportSettingsProvider,
} from '../settings/reports/weightReportSettingsProvider';
import {
  convertTimeframeToLocalTime,
  formatTimeframe,
} from '../utils/formatTimeframe';
import { getSessionRangeTimestampsForWeights } from '../utils/getSessionRangeTimestamps';
import { isValidDateRange } from '../utils/isValidDateRange';
import { IWeightAggregates, weightsProvider } from '../weightsProvider';
import { IReportState, IValidationResult } from './types';

export interface IWeightReportState extends IReportState {
  data: IComparedWeight[];
  settings: IWeightReportSettings;
  sessionMeans: ISessionMean[];
  entity: Entity;
}

interface ISettingsStageData {
  reportSettings: IWeightReportSettings;
}

export interface ISessionMean {
  timestamp: number;
  value: number;
  units: string;
}

interface IEntityQueryArg {
  id: string;
  type: EntityType;
  entity: Entity;
}

interface IEntitiesStageData extends ISettingsStageData {
  entityQueries: IEntityQueryArg[];
}
interface IWeightsData {
  entity: Entity;
  data: IWeightAggregates;
}

interface ITargetStageData extends IEntitiesStageData {
  targetData: IWeightsData[];
}

interface IBaselineStageData extends ITargetStageData {
  baselineData: IWeightsData[];
}

export interface IComparedWeight {
  targetMean: number;
  targetTimeframe: string;
  baselineTimeframe: string;
  baselineMean: number;
  changePercentage: number;
  change: number;
  entity: Entity;
  units: string;
  startTime: number;
  endTime: number;
}

interface IComparisonStageData extends ISettingsStageData {
  data: IComparedWeight[];
  sessionMeans: ISessionMean[];
}

/**
 * @description listens to weights for entities
 * @param entity
 * @returns
 */
export const weightReportProvider = (
  entity: Entity,
  entityType: EntityType
): Observable<IWeightReportState> => {
  return weightReportSettingsProvider().pipe(
    /// generate a list of objects representing the queries that need to be performed
    switchMap(
      (
        reportSettings: IWeightReportSettings
      ): Observable<IEntitiesStageData> => {
        if (entityType === 'groups' || entityType === 'teams') {
          return entitiesProvider().pipe(
            map((entities: IEntities): IEntitiesStageData => {
              /// filter the athletes that are members of the group or team
              const athletes = entities.athletes.filter(
                (a: IAthlete): boolean => a[entityType].includes(entity.id)
              );
              return {
                reportSettings,
                entityQueries: athletes.map(
                  (mEntity: IAthlete): IEntityQueryArg => ({
                    id: mEntity.id,
                    type: 'athletes',
                    entity: mEntity,
                  })
                ),
              };
            })
          );
        } else {
          ///  the selected entity is an athlete
          return of({
            reportSettings,
            entityQueries: [{ id: entity.id, type: entityType, entity }],
          });
        }
      }
    ),
    /// execute the queries for the target timeframe
    switchMap(
      (
        settingsAndQueries: IEntitiesStageData
      ): Observable<ITargetStageData> => {
        /// validate settings before attempting to run queries
        const settingsValidation = validateSettings(
          settingsAndQueries.reportSettings
        );
        if (settingsValidation.isValid) {
          if (settingsAndQueries.entityQueries.length > 0) {
            return combineLatest(
              settingsAndQueries.entityQueries.map(
                (qe: IEntityQueryArg): Observable<IWeightsData> => {
                  return weightsProvider(
                    qe.id,
                    qe.type as EntityType,
                    settingsAndQueries.reportSettings.timeframe.dateType,
                    settingsAndQueries.reportSettings.timeframe.dateRange
                  ).pipe(
                    map((data: IWeightAggregates): IWeightsData => {
                      return { entity: qe.entity, data };
                    })
                  );
                }
              )
            ).pipe(
              map((targetData: IWeightsData[]): ITargetStageData => {
                return {
                  ...settingsAndQueries,
                  targetData,
                };
              })
            );
          } else {
            const targetStageData: ITargetStageData = {
              ...settingsAndQueries,
              targetData: [],
            };
            return of(targetStageData);
          }
        } else {
          const targetStageData: ITargetStageData = {
            ...settingsAndQueries,
            targetData: [],
          };
          return of(targetStageData);
        }
      }
    ),
    /// execute the queries for the baseline timeframe
    switchMap(
      (targetStageData: ITargetStageData): Observable<IBaselineStageData> => {
        const obs = targetStageData.entityQueries.map(
          (qe: IEntityQueryArg): Observable<IWeightsData> => {
            const matchedEntity = targetStageData.targetData.find(
              (td: IWeightsData): boolean => td.entity.id === qe.id
            );
            const afterSessionId = matchedEntity.data.end;
            return weightsProvider(
              qe.id,
              qe.type as EntityType,
              targetStageData.reportSettings.baselineTimeframe.dateType,
              targetStageData.reportSettings.baselineTimeframe.dateRange,
              afterSessionId
            ).pipe(
              map((data: IWeightAggregates): IWeightsData => {
                return { entity: qe.entity, data };
              })
            );
          }
        );
        if (obs.length > 0) {
          return combineLatest(obs).pipe(
            map(
              (baselineData: IWeightsData[]): IBaselineStageData => ({
                ...targetStageData,
                baselineData,
              })
            )
          );
        } else {
          return of({
            ...targetStageData,
            baselineData: [],
          });
        }
      }
    ),
    /// run the comparison of the target timeframe and the baseline timeframe
    map((baselineStageData: IBaselineStageData): IComparisonStageData => {
      const comparedData = compareWeights(
        baselineStageData.reportSettings,
        baselineStageData.targetData,
        baselineStageData.baselineData
      );
      const sessionMeans = generateSessionMeans(
        baselineStageData.targetData,
        baselineStageData.reportSettings
      );

      const cleanedData = sortBy(
        comparedData,
        (item: IComparedWeight): number => -Math.abs(item.changePercentage)
      ).filter((item: IComparedWeight): boolean => isNumber(item.targetMean));
      return {
        reportSettings: baselineStageData.reportSettings,
        data: cleanedData,
        sessionMeans,
      };
    }),
    map((comparisonStageData: IComparisonStageData): IWeightReportState => {
      const settingsValidation = validateSettings(
        comparisonStageData.reportSettings
      );
      if (settingsValidation.isValid) {
        const dataValidation = validateData(comparisonStageData.data);
        const timeframe = getTimeframe(comparisonStageData.data);
        const localTargetTimeframe = convertTimeframeToLocalTime(
          comparisonStageData.reportSettings.timeframe
        );
        const localBaselineTimeframe = convertTimeframeToLocalTime(
          comparisonStageData.reportSettings.baselineTimeframe
        );
        return {
          data: comparisonStageData.data,
          sessionMeans: comparisonStageData.sessionMeans,
          settings: {
            ...comparisonStageData.reportSettings,
            timeframe: localTargetTimeframe,
            baselineTimeframe: localBaselineTimeframe,
          },
          entity,
          timeframe,
          ...dataValidation,
        };
      } else {
        return {
          data: [],
          sessionMeans: [],
          settings: comparisonStageData.reportSettings,
          entity,
          ...settingsValidation,
        };
      }
    })
  );
};

/**
 * @description gets timeframe from largest end time and smallest start time
 * @param data
 * @returns
 */
const getTimeframe = (data: IComparedWeight[]): string => {
  const end = maxBy(data, (item: IComparedWeight): number => item.endTime);
  const start = minBy(data, (item: IComparedWeight): number => item.startTime);
  return formatTimeframe(start?.startTime, end?.endTime);
};

/**
 * @description validates the data
 * @param data
 * @returns
 */
const validateData = (data: IComparedWeight[]): IValidationResult => {
  if (data.length === 0) {
    return {
      isValid: false,
      errorCode: 'insufficient-data',
    };
  } else {
    return {
      isValid: true,
    };
  }
};

/**
 * @description calculates the average of each session
 * @param weights
 * @returns
 */
const generateSessionMeans = (
  weights: IWeightsData[],
  settings: IWeightReportSettings
): ISessionMean[] => {
  const sessionAccumulator: {
    [sessionId: string]: { count: number; total: number };
  } = {};
  for (const weight of weights) {
    for (const session of weight.data.sessions) {
      const existing = sessionAccumulator[+session.id];
      sessionAccumulator[session.id] = {
        count: existing?.count ? existing?.count + 1 : 1,
        total: existing?.total ? existing?.total + session.mean : session.mean,
      };
    }
  }
  const converter =
    settings.units.value === 'kgs' ? convertNewtonsToKgs : convertNewtonsToLbs;
  return Object.keys(sessionAccumulator).map((key: string): ISessionMean => {
    return {
      timestamp: +key,
      value: converter(
        sessionAccumulator[key].total / sessionAccumulator[key].count
      ),
      units: settings.units.value,
    };
  });
};

/**
 * @description compares target weights to baseline weights
 * @param settings
 * @param targetData
 * @param baselineData
 * @returns
 */
const compareWeights = (
  settings: IWeightReportSettings,
  targetData: IWeightsData[],
  baselineData: IWeightsData[]
): IComparedWeight[] => {
  const comparedData: IComparedWeight[] = [];
  const converter =
    settings.units.value === 'lbs' ? convertNewtonsToLbs : convertNewtonsToKgs;
  for (const target of targetData) {
    const baseline = baselineData.find(
      (base: IWeightsData): boolean => base.entity.id === target.entity.id
    );
    const targetMean = target.data?.combined?.mean;
    const baselineMean = baseline.data?.combined?.mean;
    const change = targetMean - baselineMean;
    const changePercentage = (change / baselineMean) * 100;
    const { start: startTarget, end: endTarget } =
      getSessionRangeTimestampsForWeights(target.data?.sessions ?? []);
    const { start: startBaseline, end: endBaseline } =
      getSessionRangeTimestampsForWeights(baseline.data?.sessions ?? []);

    comparedData.push({
      targetMean: roundValue(converter(targetMean), 1),
      targetTimeframe: formatTimeframe(startTarget, endTarget),
      baselineTimeframe: formatTimeframe(startBaseline, endBaseline),
      baselineMean: roundValue(converter(baselineMean), 1),
      entity: target.entity,
      change: roundValue(converter(change), 1),
      changePercentage: roundValue(changePercentage, 1),
      units: settings.units.value,
      startTime: target.data.start,
      endTime: target.data.end,
    });
  }
  return comparedData;
};

/**
 * @description validates the user settings for the report
 * @param settings
 * @returns
 */
const validateSettings = (
  settings: IWeightReportSettings
): IValidationResult => {
  /// check to make sure timeframe is valid
  if (
    !isValidDateRange({
      dateType: settings?.timeframe?.dateType,
      dateRange: settings?.timeframe?.dateRange,
    })
  ) {
    return {
      isValid: false,
      errorCode: 'invalid-date-range',
    };
  }
  /// check to make sure baseline timeframe is valid
  if (
    !isValidDateRange({
      dateType: settings?.baselineTimeframe?.dateType,
      dateRange: settings?.baselineTimeframe?.dateRange,
    })
  ) {
    return {
      isValid: false,
      errorCode: 'invalid-baseline-date-range',
    };
  }

  return { isValid: true };
};
